import React, { useState, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatINR, formatIndianDate } from '../../../utils/currency';

const WarrantyTimelineChart = ({ assets = [] }) => {
  const [timeHorizon, setTimeHorizon] = useState(12); // 6 or 12 months
  const [showProjections, setShowProjections] = useState(true);

  // Process upcoming warranty data from assets for the next N months
  const warrantyData = useMemo(() => {
    const months = {};
    const now = new Date();
    
    // Generate upcoming N months starting from current month
    for (let i = 0; i < timeHorizon; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const monthKey = date.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
      months[monthKey] = {
        month: monthKey,
        expiring: 0,
        cost: 0,
        projected: 0,
        critical: 0
      };
    }

    // Process assets with warranty expiration
    assets.forEach(asset => {
      if (!asset.warranty_expiration) return;
      
      const expiryDate = new Date(asset.warranty_expiration);
      const daysDiff = (expiryDate - now) / (1000 * 60 * 60 * 24);
      
      // Look forward up to timeHorizon * 31 days
      if (daysDiff < 0 || daysDiff > (timeHorizon * 31)) return;
      
      const monthKey = expiryDate.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
      
      if (months[monthKey]) {
        months[monthKey].expiring++;
        const cost = parseFloat(asset.purchase_price) || parseFloat(asset.price) || 0;
        months[monthKey].cost += cost;
        if (daysDiff <= 30) {
          months[monthKey].critical++;
        }
      }
    });

    const data = Object.values(months);
    // Project dynamic trend
    let avg = data.reduce((s, d) => s + d.expiring, 0) / (data.length || 1);
    data.forEach((item) => {
      item.projected = item.expiring > 0 ? item.expiring : Math.max(0, Math.round(avg * 0.8));
    });

    return data;
  }, [assets, timeHorizon]);

  // Get upcoming expirations within next 90 days
  const upcomingExpirations = useMemo(() => {
    const now = new Date();
    return assets
      .filter(asset => asset.warranty_expiration)
      .map(asset => {
        const expiryDate = new Date(asset.warranty_expiration);
        const daysLeft = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
        return {
          id: asset.id,
          assetId: asset.asset_id,
          assetName: asset.name || `${asset.brand || ''} ${asset.model || ''}`.trim() || 'Unnamed Asset',
          department: asset.department || 'Unassigned',
          expiryDate: asset.warranty_expiration,
          daysLeft: daysLeft,
          replacementCost: parseFloat(asset.purchase_price) || parseFloat(asset.price) || 0,
          priority: daysLeft <= 15 ? 'high' : daysLeft <= 45 ? 'medium' : 'low'
        };
      })
      .filter(item => item.daysLeft >= 0 && item.daysLeft <= 90)
      .sort((a, b) => a.daysLeft - b.daysLeft)
      .slice(0, 6);
  }, [assets]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevated">
          <p className="font-semibold text-popover-foreground mb-1">{label}</p>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between space-x-4">
              <span className="text-muted-foreground">Expiring Assets:</span>
              <span className="font-bold text-primary">{data?.expiring}</span>
            </div>
            {data?.cost > 0 && (
              <div className="flex justify-between space-x-4">
                <span className="text-muted-foreground">Estimated Value:</span>
                <span className="font-bold text-success">{formatINR(data?.cost, false)}</span>
              </div>
            )}
            {data?.critical > 0 && (
              <div className="flex justify-between space-x-4">
                <span className="text-error font-medium">Critical Expirations:</span>
                <span className="font-bold text-error">{data?.critical}</span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-error bg-error/10 border-error/20';
      case 'medium':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'low':
        return 'text-success bg-success/10 border-success/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const totalExpiring = warrantyData.reduce((sum, item) => sum + item.expiring, 0);
  const totalCost = warrantyData.reduce((sum, item) => sum + item.cost, 0);
  const totalCritical = warrantyData.reduce((sum, item) => sum + item.critical, 0);

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">Warranty Expiration Timeline</h3>
          <p className="text-sm text-muted-foreground">Upcoming warranty expirations & replacement projections</p>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* Horizon Toggle */}
          <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/40">
            <button
              onClick={() => setTimeHorizon(6)}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-smooth ${
                timeHorizon === 6 ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              6 Months
            </button>
            <button
              onClick={() => setTimeHorizon(12)}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-smooth ${
                timeHorizon === 12 ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              12 Months
            </button>
          </div>

          <Button
            variant={showProjections ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowProjections(!showProjections)}
            iconName="TrendingUp"
            iconPosition="left"
          >
            Trend
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Chart Section */}
        <div className="xl:col-span-2">
          <div className="h-80 mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={warrantyData} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
                <defs>
                  <linearGradient id="warrantyExpGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563EB" stopOpacity={0.35}/>
                    <stop offset="95%" stopColor="#2563EB" stopOpacity={0.02}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 11 }}
                  stroke="#64748B"
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  stroke="#64748B"
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                
                <Area
                  type="monotone"
                  dataKey="expiring"
                  stroke="#2563EB"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#warrantyExpGradient)"
                  name="Expiring Assets"
                />
                
                {showProjections && (
                  <Line
                    type="monotone"
                    dataKey="projected"
                    stroke="#059669"
                    strokeDasharray="4 4"
                    strokeWidth={1.5}
                    dot={false}
                    name="Projected Trend"
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-primary/5 rounded-lg border border-primary/20">
              <div className="text-2xl font-bold text-primary">{totalExpiring}</div>
              <div className="text-xs text-muted-foreground">Expiring ({timeHorizon}M)</div>
            </div>
            <div className="text-center p-3 bg-error/5 rounded-lg border border-error/20">
              <div className="text-2xl font-bold text-error">{formatINR(totalCost, true)}</div>
              <div className="text-xs text-muted-foreground">Replacement Value</div>
            </div>
            <div className="text-center p-3 bg-warning/5 rounded-lg border border-warning/20">
              <div className="text-2xl font-bold text-warning">{totalCritical}</div>
              <div className="text-xs text-muted-foreground">Expiring in 30 Days</div>
            </div>
          </div>
        </div>

        {/* Upcoming Expirations */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-card-foreground">Upcoming Expirations</h4>
            <span className="text-xs text-muted-foreground">Next 90 Days</span>
          </div>
          
          <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
            {upcomingExpirations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                <Icon name="CheckCircle2" size={32} className="text-success mx-auto mb-2 opacity-75" />
                No warranties expiring in the next 90 days.
              </div>
            ) : (
              upcomingExpirations.map((item) => (
                <div
                  key={item?.id}
                  className="p-3 border border-border rounded-lg hover:border-primary/50 transition-smooth bg-card"
                >
                  <div className="flex items-start justify-between mb-1.5">
                    <div className="flex-1 min-w-0 pr-2">
                      <div className="flex items-center space-x-2 mb-0.5">
                        <span className="text-sm font-semibold text-card-foreground truncate" title={item?.assetName}>
                          {item?.assetName}
                        </span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border uppercase ${getPriorityColor(item?.priority)}`}>
                          {item?.priority}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {item?.assetId} • {item?.department}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs pt-1 border-t border-border/40">
                    <div className="flex items-center space-x-1 text-muted-foreground">
                      <Icon name="Calendar" size={13} className="text-warning" />
                      <span>{item?.daysLeft} days left ({formatIndianDate(item?.expiryDate)})</span>
                    </div>
                    <div className="font-semibold text-card-foreground">
                      {formatINR(item?.replacementCost, true)}
                    </div>
                  </div>
                  
                  {/* Progress Indicator */}
                  <div className="mt-2 bg-muted rounded-full h-1.5 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        item?.daysLeft <= 15 ? 'bg-error' :
                        item?.daysLeft <= 45 ? 'bg-warning' : 'bg-success'
                      }`}
                      style={{ width: `${Math.max(5, Math.min(100, ((90 - item?.daysLeft) / 90) * 100))}%` }}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarrantyTimelineChart;
